import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { toCsv, exportFileName } from '@shared/csv'
import { api, ApiError, type MetricsResponse } from '../lib/api'

function formatMinutes(minutes: number | null): string {
  if (minutes === null) return '—'
  if (minutes < 60) return `${Math.round(minutes)}m`
  const hours = minutes / 60
  return hours < 24 ? `${hours.toFixed(1)}h` : `${Math.round(hours / 24)}d`
}

/**
 * The screen a manager opens in the morning.
 *
 * It answers "is the team keeping up", not "what did we buy" — so the figures are volume,
 * what is owed a reply, what has already breached, and how each person is doing. Capacity
 * and spend live on the billing screen, where a decision about them can actually be made.
 */
export function MetricsDashboard({
  workspaceName,
  industryName,
  onGoToInbox,
}: {
  workspaceName: string
  industryName: string
  onGoToInbox: () => void
}) {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [labelIds, setLabelIds] = useState<string[]>([])
  const [kinds, setKinds] = useState<string[]>([])
  const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>([])
  const [match, setMatch] = useState<'all' | 'any'>('all')
  const [propertyFilters, setPropertyFilters] = useState<Record<string, string[]>>({})

  const filters = { dateFrom, dateTo, labelIds, kind: kinds, assigneeUserIds, match, propertyFilters }
  const team = useQuery({ queryKey: ['team'], queryFn: api.team, retry: false })
  // Only single/multi-select defs have a fixed set of values worth a checkbox facet — text,
  // number, date, file and cascading properties have no bounded option list to render one from.
  const propertyDefs = useQuery({ queryKey: ['property-defs'], queryFn: api.propertyDefs, retry: false })
  const filterableDefs = (propertyDefs.data?.defs ?? []).filter(
    (d) => (d.type === 'single_select' || d.type === 'multi_select') && d.options.length > 0,
  )
  const metrics = useQuery({
    queryKey: ['metrics', filters],
    queryFn: () => api.metrics(filters),
    retry: false,
  })

  if (metrics.isPending) {
    return <div className="cad-card" style={{ height: '220px' }} />
  }

  if (metrics.isError) {
    const message =
      metrics.error instanceof ApiError ? metrics.error.message : 'Metrics unavailable.'
    // The one cause worth a dedicated exit: a trial with no database yet has nothing to show
    // on any screen, so point straight at the connect-database step instead of a dead end.
    const needsDatabase = metrics.error instanceof ApiError && metrics.error.status === 409
    return (
      <div className="cad-card stack gap-3">
        <p className="cad-muted">{message}</p>
        {needsDatabase && (
          <button type="button" className="cad-btn" onClick={onGoToInbox}>
            Connect your database
          </button>
        )}
      </div>
    )
  }

  const data = metrics.data as MetricsResponse
  const { volume, tickets, flagged, categories, labels, history, messageVolume, agents } = data

  // One health verdict for the whole screen, so the state is legible before reading a figure.
  const health =
    tickets.breaching > 0
      ? { tone: 'bad' as const, label: `${tickets.breaching} breaching` }
      : tickets.open > 0
        ? { tone: 'warn' as const, label: `${tickets.open} awaiting reply` }
        : { tone: 'good' as const, label: 'All clear' }

  const hitRate =
    tickets.answered > 0
      ? Math.round((tickets.answeredWithinTarget / tickets.answered) * 100)
      : null

  const busiest = categories[0]?.total ?? 1

  // A weekly reporting habit needs a file to attach, not just numbers on a screen — the same
  // gap flagged against every dashboard-only competitor screen this one was benchmarked against.
  const exportCsv = () => {
    const summary = toCsv(
      ['Metric', 'Value'],
      [
        ['Conversations', volume.conversations],
        ['Unread', volume.unread],
        ['Messages in', volume.messagesIn],
        ['Messages out', volume.messagesOut],
        ['Open tickets', tickets.open],
        ['Breaching tickets', tickets.breaching],
        ['Closed tickets', tickets.closed],
        ['Median first response (min)', tickets.medianFirstResponseMinutes ?? ''],
        ['Answered within target (%)', hitRate ?? ''],
      ],
    )
    const agentRows = toCsv(
      ['Agent', 'Replies', 'Notes', 'Open tickets', 'Closed tickets', 'Median first response (min)', 'Avg resolution (min)'],
      agents.map((a) => [
        a.name,
        a.sent,
        a.notes,
        a.openTickets,
        a.closedTickets,
        a.medianFirstResponseMinutes ?? '',
        a.avgResolutionMinutes ?? '',
      ]),
    )
    const csv = `${summary}\r\n${agentRows}`
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = exportFileName('metrics', new Date())
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">
          {industryName} · {data.scoped ? 'Your queues' : 'All queues'}
        </span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">{workspaceName}</h1>
          <div className="row gap-2" style={{ alignItems: 'center' }}>
            <span className="cad-pill" data-tone={health.tone}>
              {health.label}
            </span>
            <button type="button" className="cad-btn" data-variant="quiet" onClick={exportCsv}>
              Export CSV
            </button>
          </div>
        </div>
      </header>

      <section className="cad-card">
        <div className="stack gap-3">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">Filter</h2>
            {(dateFrom ||
              dateTo ||
              labelIds.length > 0 ||
              kinds.length > 0 ||
              assigneeUserIds.length > 0 ||
              Object.values(propertyFilters).some((v) => v.length > 0)) && (
              <button
                type="button"
                className="cad-btn"
                data-variant="text"
                onClick={() => {
                  setDateFrom('')
                  setDateTo('')
                  setLabelIds([])
                  setKinds([])
                  setAssigneeUserIds([])
                  setPropertyFilters({})
                }}
              >
                Clear filters
              </button>
            )}
          </div>
          <div className="row gap-3 wrap" style={{ alignItems: 'flex-end' }}>
            <label className="stack gap-1">
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>From</span>
              <input className="cad-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </label>
            <label className="stack gap-1">
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>To</span>
              <input className="cad-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </label>
            <div className="stack gap-1">
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Chat type</span>
              <div className="row gap-2">
                {(['direct', 'group'] as const).map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    className="label-swatch"
                    aria-pressed={kinds.includes(kind)}
                    data-selected={kinds.includes(kind)}
                    onClick={() =>
                      setKinds((current) =>
                        current.includes(kind) ? current.filter((k) => k !== kind) : [...current, kind],
                      )
                    }
                  >
                    {kind === 'direct' ? 'Direct' : 'Group'}
                  </button>
                ))}
              </div>
            </div>
            {(team.isError || propertyDefs.isError) && (
              <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                Some filters could not load — labels, assignees, or properties may be missing below.
              </p>
            )}
            {team.data && team.data.labels.length > 0 && (
              <div className="stack gap-1">
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Labels</span>
                <div className="row gap-2 wrap">
                  {team.data.labels.map((label) => (
                    <button
                      key={label.id}
                      type="button"
                      className="label-swatch"
                      aria-pressed={labelIds.includes(label.id)}
                      data-selected={labelIds.includes(label.id)}
                      onClick={() =>
                        setLabelIds((current) =>
                          current.includes(label.id) ? current.filter((id) => id !== label.id) : [...current, label.id],
                        )
                      }
                    >
                      <span className="label-dot" style={{ background: label.color }} aria-hidden="true" />
                      {label.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {team.data && team.data.members.length > 0 && (
              <div className="stack gap-1">
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Assignee</span>
                <div className="row gap-2 wrap">
                  {team.data.members.map((member) => (
                    <button
                      key={member.userId}
                      type="button"
                      className="label-swatch"
                      aria-pressed={assigneeUserIds.includes(member.userId)}
                      data-selected={assigneeUserIds.includes(member.userId)}
                      onClick={() =>
                        setAssigneeUserIds((current) =>
                          current.includes(member.userId)
                            ? current.filter((id) => id !== member.userId)
                            : [...current, member.userId],
                        )
                      }
                    >
                      {member.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {filterableDefs.map((def) => (
              <div key={def.id} className="stack gap-1">
                <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>{def.name}</span>
                <div className="row gap-2 wrap">
                  {def.options.map((option) => {
                    const selected = (propertyFilters[def.id] ?? []).includes(option)
                    return (
                      <button
                        key={option}
                        type="button"
                        className="label-swatch"
                        aria-pressed={selected}
                        data-selected={selected}
                        onClick={() =>
                          setPropertyFilters((current) => {
                            const forDef = current[def.id] ?? []
                            return {
                              ...current,
                              [def.id]: selected ? forDef.filter((v) => v !== option) : [...forDef, option],
                            }
                          })
                        }
                      >
                        {option}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
            <div className="stack gap-1">
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>Match</span>
              <div className="row gap-2">
                {(['all', 'any'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    className="label-swatch"
                    aria-pressed={match === option}
                    data-selected={match === option}
                    onClick={() => setMatch(option)}
                  >
                    {option === 'all' ? 'All conditions' : 'Any condition'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {data.filters.applied && (
            <p className="cad-note">
              Filters applied — figures below are scoped to the matching chats
              {dateFrom || dateTo ? ' and date range' : ''}, not the whole workspace.
            </p>
          )}
        </div>
      </section>

      <div className="cad-stats">
        <Stat label="Conversations" value={volume.conversations.toLocaleString()} />
        <Stat label="Incoming messages" value={volume.messagesIn.toLocaleString()} />
        <Stat
          label="Awaiting reply"
          value={String(tickets.open)}
          tone={tickets.open > 0 ? 'bad' : 'good'}
        />
        <Stat
          label="Unassigned"
          value={String(tickets.unassigned)}
          tone={tickets.unassigned > 0 ? 'bad' : 'good'}
        />
        <Stat
          label="Median first response"
          value={formatMinutes(tickets.medianFirstResponseMinutes)}
        />
        <Stat
          label="Answered in target"
          value={hitRate === null ? '—' : `${hitRate}%`}
          tone={hitRate !== null && hitRate >= 90 ? 'good' : hitRate !== null ? 'bad' : undefined}
        />
        <Stat
          label="Flagged response time"
          value={flagged.count === 0 ? '—' : formatMinutes(flagged.medianResponseMinutes)}
        />
      </div>

      <section className="cad-card">
        <div className="stack gap-3">
          <h2 className="cad-section-title">Message volume</h2>
          <MessageVolumeChart history={messageVolume} />
          <p className="cad-note">
            A different lens than the ticket chart below: how much traffic actually moved
            through the inbox, including days nothing escalated to a ticket at all.
          </p>
        </div>
      </section>

      <section className="cad-card">
        <div className="stack gap-3">
          <h2 className="cad-section-title">Ticket history</h2>
          <TicketHistoryChart history={history} />
          <p className="cad-note">
            Created and closed are what happened that day; the backlog line is a running total —
            everything opened and not yet closed, carried forward from before this window too.
          </p>
        </div>
      </section>

      <section className="cad-card">
        <div className="stack gap-3">
          <h2 className="cad-section-title">Resolution time</h2>
          <ResolutionBucketsChart buckets={tickets.resolutionBuckets} />
          <p className="cad-note">
            A median hides the tail — this is how resolved tickets actually spread across how
            long they took, from a same-hour close to one that sat for over a week.
          </p>
        </div>
      </section>

      <div className="metrics-split">
        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">Where the load is</h2>
            {categories.length === 0 ? (
              <p className="cad-muted">Nothing routed yet.</p>
            ) : (
              <div>
                {categories.map((category) => (
                  <div key={category.name} className="cad-meter-row">
                    <div className="cad-meter-head">
                      <span>{category.name}</span>
                      <b>
                        {category.total}
                        {category.breaching > 0 && (
                          <span className="cad-muted"> · {category.breaching} late</span>
                        )}
                      </b>
                    </div>
                    <div className="cad-meter">
                      <span
                        // Width is share of the busiest category, so the bars compare
                        // against each other rather than an arbitrary ceiling.
                        style={{ width: `${Math.max(4, (category.total / busiest) * 100)}%` }}
                        data-tone={category.breaching > 0 ? 'warn' : 'good'}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="cad-note">
              Categories come from your {industryName} rules. A late count means the reply
              target passed before anyone answered.
            </p>
          </div>
        </section>

        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">By label</h2>
            {labels.length === 0 ? (
              <p className="cad-muted">No labelled tickets yet.</p>
            ) : (
              <div>
                {labels.map((label) => (
                  <div key={label.name} className="cad-meter-row">
                    <div className="cad-meter-head">
                      <span>{label.name}</span>
                      <b>
                        {label.total}
                        {label.breaching > 0 && (
                          <span className="cad-muted"> · {label.breaching} late</span>
                        )}
                      </b>
                    </div>
                    <div className="cad-meter">
                      <span
                        style={{ width: `${Math.max(4, (label.total / Math.max(1, labels[0].total)) * 100)}%` }}
                        data-tone={label.breaching > 0 ? 'warn' : 'good'}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="cad-note">
              A ticket with no chat label does not appear here. A ticket can carry more than
              one label, so totals across labels do not have to add up to the ticket count.
            </p>
          </div>
        </section>

        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">Team</h2>
            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th className="num">Replies</th>
                    <th className="num">Notes</th>
                    <th className="num">Open</th>
                    <th className="num">Closed</th>
                    <th className="num">Median first response</th>
                    <th className="num">Avg. resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr key={agent.userId}>
                      <td>{agent.name}</td>
                      <td className="num">{agent.sent}</td>
                      <td className="num cad-muted">{agent.notes}</td>
                      <td className="num">{agent.openTickets}</td>
                      <td className="num cad-muted">{agent.closedTickets}</td>
                      <td className="num cad-muted">{formatMinutes(agent.medianFirstResponseMinutes)}</td>
                      <td className="num cad-muted">{formatMinutes(agent.avgResolutionMinutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="cad-note">
              Notes are internal and never reach the customer, so they are counted apart from
              replies. Median first response only counts tickets this member was the first to
              answer; average resolution only counts a member's own closed tickets.
            </p>
          </div>
        </section>
      </div>

      {tickets.breaching > 0 && (
        <div className="cad-card cad-card-feature">
          <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
            <div className="stack gap-1">
              <span className="cad-eyebrow" style={{ color: 'var(--cad-warn)' }}>
                Needs attention
              </span>
              <strong style={{ fontSize: 'var(--cad-text-lg)' }}>
                {tickets.breaching} {tickets.breaching === 1 ? 'conversation has' : 'conversations have'}{' '}
                passed the reply target
              </strong>
              <span style={{ opacity: 0.7, fontSize: 'var(--cad-text-md)' }}>
                {volume.unread} unread message{volume.unread === 1 ? '' : 's'} across your queues.
              </span>
            </div>
            <button type="button" className="cad-btn" onClick={onGoToInbox}>
              Open inbox
            </button>
          </div>
        </div>
      )}
    </>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'good' | 'bad'
}) {
  return (
    <div className="cad-stat">
      <span className="cad-stat-label">{label}</span>
      <span className="cad-stat-value" data-tone={tone}>
        {value}
      </span>
    </div>
  )
}

const RESOLUTION_BUCKETS: { key: 'underHour' | 'underDay' | 'underWeek' | 'overWeek'; label: string }[] = [
  { key: 'underHour', label: 'Under an hour' },
  { key: 'underDay', label: 'Under a day' },
  { key: 'underWeek', label: 'Under a week' },
  { key: 'overWeek', label: 'Over a week' },
]

function ResolutionBucketsChart({
  buckets,
}: {
  buckets: { underHour: number; underDay: number; underWeek: number; overWeek: number }
}) {
  const total = buckets.underHour + buckets.underDay + buckets.underWeek + buckets.overWeek
  if (total === 0) return <p className="cad-muted">No resolved tickets yet.</p>

  const busiest = Math.max(...RESOLUTION_BUCKETS.map((b) => buckets[b.key]))
  return (
    <div>
      {RESOLUTION_BUCKETS.map((bucket) => (
        <div key={bucket.key} className="cad-meter-row">
          <div className="cad-meter-head">
            <span>{bucket.label}</span>
            <b>{buckets[bucket.key]}</b>
          </div>
          <div className="cad-meter">
            <span
              style={{ width: `${Math.max(4, (buckets[bucket.key] / Math.max(1, busiest)) * 100)}%` }}
              data-tone={bucket.key === 'overWeek' && buckets[bucket.key] > 0 ? 'warn' : 'good'}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Incoming and outgoing as bars, flagged as the number over each day — same visual language
 *  as the ticket-history chart below it, deliberately, for the reason given there. */
function MessageVolumeChart({
  history,
}: {
  history: { date: string; incoming: number; outgoing: number; flagged: number }[]
}) {
  if (history.every((day) => day.incoming === 0 && day.outgoing === 0)) {
    return <p className="cad-muted">No message activity in this window.</p>
  }

  const tallest = Math.max(1, ...history.flatMap((day) => [day.incoming, day.outgoing]))
  const barMaxHeight = 72

  return (
    <div className="scroll-x">
      <div className="row gap-1" style={{ alignItems: 'flex-end', paddingBottom: '4px' }}>
        {history.map((day) => (
          <div
            key={day.date}
            className="stack"
            style={{ alignItems: 'center', gap: '4px', minWidth: '26px' }}
            title={`${day.date} — ${day.incoming} in, ${day.outgoing} out, ${day.flagged} flagged`}
          >
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-2xs)' }}>{day.flagged || ''}</span>
            <div className="row gap-1" style={{ alignItems: 'flex-end', height: `${barMaxHeight}px` }}>
              <span
                style={{
                  display: 'block',
                  width: '7px',
                  borderRadius: '2px',
                  background: 'var(--cad-accent)',
                  height: `${Math.max(2, (day.incoming / tallest) * barMaxHeight)}px`,
                }}
              />
              <span
                style={{
                  display: 'block',
                  width: '7px',
                  borderRadius: '2px',
                  background: 'var(--cad-good)',
                  height: `${Math.max(2, (day.outgoing / tallest) * barMaxHeight)}px`,
                }}
              />
            </div>
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-2xs)' }}>{day.date.slice(5)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Created and closed as bars, backlog as the number over each day — a sparkline would show
 * the backlog's shape better, but every other chart in this product is a plain bar, and a
 * third visual language on one screen is worse than one chart being slightly less ideal.
 */
function TicketHistoryChart({
  history,
}: {
  history: { date: string; created: number; closed: number; unresolved: number }[]
}) {
  if (history.every((day) => day.created === 0 && day.closed === 0 && day.unresolved === 0)) {
    return <p className="cad-muted">No ticket activity in the last 30 days.</p>
  }

  const tallest = Math.max(1, ...history.flatMap((day) => [day.created, day.closed]))
  const barMaxHeight = 72

  return (
    <div className="scroll-x">
      <div className="row gap-1" style={{ alignItems: 'flex-end', paddingBottom: '4px' }}>
        {history.map((day) => (
          <div
            key={day.date}
            className="stack"
            style={{ alignItems: 'center', gap: '4px', minWidth: '26px' }}
            title={`${day.date} — ${day.created} opened, ${day.closed} closed, ${day.unresolved} backlog`}
          >
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-2xs)' }}>{day.unresolved}</span>
            <div className="row gap-1" style={{ alignItems: 'flex-end', height: `${barMaxHeight}px` }}>
              <span
                style={{
                  display: 'block',
                  width: '7px',
                  borderRadius: '2px',
                  background: 'var(--cad-warn)',
                  height: `${Math.max(2, (day.created / tallest) * barMaxHeight)}px`,
                }}
              />
              <span
                style={{
                  display: 'block',
                  width: '7px',
                  borderRadius: '2px',
                  background: 'var(--cad-good)',
                  height: `${Math.max(2, (day.closed / tallest) * barMaxHeight)}px`,
                }}
              />
            </div>
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-2xs)' }}>{day.date.slice(5)}</span>
          </div>
        ))}
      </div>
      <div className="row gap-4" style={{ marginTop: '8px', fontSize: 'var(--cad-text-sm)' }}>
        <span className="row gap-1" style={{ alignItems: 'center' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'var(--cad-warn)' }} />
          Opened
        </span>
        <span className="row gap-1" style={{ alignItems: 'center' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'var(--cad-good)' }} />
          Closed
        </span>
        <span className="cad-muted">Number above each day is the backlog at day's end.</span>
      </div>
    </div>
  )
}
